import axios from 'axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put } from 'redux-saga/effects';

/*========== API CALLS =============*/

const getNotificationsApi = (params) => axios.get('/notification', { params });
const getUnreadCountApi = () => axios.get('/notification/unreadcount');
const markAsReadApi = (id) => axios.put(`/notification/${id}/read`);
const markAsUnreadApi = (id) => axios.put(`/notification/${id}/unread`);
const markAllAsReadApi = () => axios.put('/notification/readall');
const archiveNotificationApi = (id) => axios.put(`/notification/${id}/archive`);
const deleteNotificationApi = (id) => axios.delete(`/notification/${id}`);
const getNotificationSettingsApi = () => axios.get('/notification/settings');
const getPreferencesApi = () => axios.get('/notification/preferences');
const updatePreferencesApi = (data) => axios.put('/notification/preferences', data);

/*========== HELPER =============*/

function* handleApiCall(apiCall, successAction, errorAction) {
  try {
    const response = yield call(apiCall);
    if (response?.data?.body) {
      yield put({ type: successAction, payload: response.data.body });
      return response.data.body;
    }
    throw new Error(response?.data?.message || 'Unknown error');
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error.message || 'Something went wrong';
    EventBus.publish('error', errorMessage);
    if (errorAction) {
      yield put({ type: errorAction, payload: errorMessage });
    }
    return null;
  }
}

/*========== SAGAS =============*/

function* getNotifications({ payload }) {
  const { filter = 'unread', page = 1, limit = 20 } = payload || {};
  const result = yield handleApiCall(
    () => getNotificationsApi({ filter, page, limit }),
    null,
    'GET_NOTIFICATIONS_FAILURE'
  );
  if (result) {
    result.filter = filter;
    result.page = page;
    yield put({ type: 'GET_NOTIFICATIONS_SUCCESS', payload: result });
  }
}

function* getUnreadCount() {
  yield handleApiCall(
    getUnreadCountApi,
    'GET_UNREAD_COUNT_SUCCESS',
    null
  );
}

function* markAsRead({ payload }) {
  yield handleApiCall(
    () => markAsReadApi(payload),
    'MARK_AS_READ_SUCCESS',
    null
  );
}

function* markAsUnread({ payload }) {
  yield handleApiCall(
    () => markAsUnreadApi(payload),
    'MARK_AS_UNREAD_SUCCESS',
    null
  );
}

function* markAllAsRead() {
  const result = yield handleApiCall(
    markAllAsReadApi,
    'MARK_ALL_AS_READ_SUCCESS',
    null
  );
  if (result !== null) {
    yield put({ type: 'MARK_ALL_AS_READ_SUCCESS' });
  }
}

function* archiveNotification({ payload }) {
  const notification = payload;
  const result = yield handleApiCall(
    () => archiveNotificationApi(notification._id || notification),
    'ARCHIVE_NOTIFICATION_SUCCESS',
    null
  );
  if (result) {
    result.wasUnread = !notification.isRead;
    yield put({ type: 'ARCHIVE_NOTIFICATION_SUCCESS', payload: result });
  }
}

function* deleteNotificationSaga({ payload }) {
  const notification = payload;
  const id = notification._id || notification;
  const result = yield handleApiCall(
    () => deleteNotificationApi(id),
    'DELETE_NOTIFICATION_SUCCESS',
    null
  );
  if (result !== null) {
    yield put({ type: 'DELETE_NOTIFICATION_SUCCESS', payload: { _id: id, wasUnread: !notification.isRead } });
  }
}

function* getNotificationSettings() {
  yield handleApiCall(
    getNotificationSettingsApi,
    'GET_NOTIFICATION_SETTINGS_SUCCESS',
    null
  );
}

function* getPreferences() {
  yield handleApiCall(
    getPreferencesApi,
    'GET_NOTIFICATION_PREFERENCES_SUCCESS',
    null
  );
}

function* updatePreferences({ payload }) {
  const result = yield handleApiCall(
    () => updatePreferencesApi(payload),
    'UPDATE_NOTIFICATION_PREFERENCES_SUCCESS',
    null
  );
  if (result) {
    EventBus.publish('success', 'Notification preferences updated');
  }
}

/*========== WATCHERS =============*/

function* watchGetNotifications() { yield takeEvery('GET_NOTIFICATIONS', getNotifications); }
function* watchGetUnreadCount() { yield takeEvery('GET_UNREAD_COUNT', getUnreadCount); }
function* watchMarkAsRead() { yield takeEvery('MARK_AS_READ', markAsRead); }
function* watchMarkAsUnread() { yield takeEvery('MARK_AS_UNREAD', markAsUnread); }
function* watchMarkAllAsRead() { yield takeEvery('MARK_ALL_AS_READ', markAllAsRead); }
function* watchArchiveNotification() { yield takeEvery('ARCHIVE_NOTIFICATION', archiveNotification); }
function* watchDeleteNotification() { yield takeEvery('DELETE_NOTIFICATION', deleteNotificationSaga); }
function* watchGetNotificationSettings() { yield takeEvery('GET_NOTIFICATION_SETTINGS', getNotificationSettings); }
function* watchGetPreferences() { yield takeEvery('GET_NOTIFICATION_PREFERENCES', getPreferences); }
function* watchUpdatePreferences() { yield takeEvery('UPDATE_NOTIFICATION_PREFERENCES', updatePreferences); }

export default function* notificationSagas() {
  yield all([
    watchGetNotifications(),
    watchGetUnreadCount(),
    watchMarkAsRead(),
    watchMarkAsUnread(),
    watchMarkAllAsRead(),
    watchArchiveNotification(),
    watchDeleteNotification(),
    watchGetNotificationSettings(),
    watchGetPreferences(),
    watchUpdatePreferences()
  ]);
}
